import React, { lazy } from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Locale, useLocale } from '../components/LocaleProvider';
import { SiteLayout } from '../components/SiteLayout';
type Copy = Record<Locale, string>;
type Department = {
  id: string;
  name: Copy;
  detail: Copy;
  image: string;
};
const departments: Department[] = [{
  id: 'fresh-fruits-vegetables',
  name: {
    fr: 'Fruits & légumes frais',
    en: 'Fresh fruit & vegetables'
  },
  detail: {
    fr: 'Des produits colorés, des herbes et de la fraîcheur chaque jour.',
    en: 'Colourful produce, herbs and daily freshness.'
  },
  image: 'https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=900&q=90'
}, {
  id: 'food-and-pantry',
  name: {
    fr: 'Épicerie',
    en: 'Food & pantry'
  },
  detail: {
    fr: 'Riz, huiles, conserves, pâtes et essentiels du placard.',
    en: 'Rice, oils, canned foods, pasta and pantry staples.'
  },
  image: 'https://images.unsplash.com/photo-1601050690597-df0568f70950?auto=format&fit=crop&w=1400&q=95'
}, {
  id: 'electricals-and-appliances',
  name: {
    fr: 'Électroménager & appareils',
    en: 'Electricals & appliances'
  },
  detail: {
    fr: 'Éclairage, électronique, appareils et accessoires.',
    en: 'Lighting, electronics, appliances and accessories.'
  },
  image: 'https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&w=900&q=90'
}, {
  id: 'bedding-and-home-textiles',
  name: {
    fr: 'Linge de maison',
    en: 'Bedding & home textiles'
  },
  detail: {
    fr: 'Draps, oreillers, serviettes et confort de la maison.',
    en: 'Bedsheets, pillows, towels and home comfort.'
  },
  image: 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=900&q=90'
}, {
  id: 'diy-and-hardware',
  name: {
    fr: 'Bricolage & quincaillerie',
    en: 'DIY & hardware'
  },
  detail: {
    fr: 'Outils, kits de réparation, aménagement et jardin.',
    en: 'Tools, repair kits, home improvement and garden.'
  },
  image: 'https://images.unsplash.com/photo-1452860606245-08befc0ff44b?auto=format&fit=crop&w=900&q=90'
}, {
  id: 'health-and-medical',
  name: {
    fr: 'Santé & parapharmacie',
    en: 'Health & medical'
  },
  detail: {
    fr: 'Premiers soins, matériel médical et soin personnel.',
    en: 'First aid, medical equipment and personal care.'
  },
  image: 'https://images.unsplash.com/photo-1584515933487-779824d29309?auto=format&fit=crop&w=900&q=90'
}, {
  id: 'school-and-library',
  name: {
    fr: 'Scolaire & librairie',
    en: 'School & library'
  },
  detail: {
    fr: 'Livres, papeterie, apprentissage et bureau.',
    en: 'Books, stationery, learning and office essentials.'
  },
  image: 'https://images.unsplash.com/photo-1507842217343-583bb7270b66?auto=format&fit=crop&w=900&q=90'
}, {
  id: 'kitchen-and-dining',
  name: {
    fr: 'Cuisine & table',
    en: 'Kitchen & dining'
  },
  detail: {
    fr: 'Ustensiles, vaisselle et indispensables du quotidien.',
    en: 'Cookware, tableware and practical kitchen essentials.'
  },
  image: 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&w=900&q=90'
}, {
  id: 'meat-poultry-and-seafood',
  name: {
    fr: 'Boucherie, volaille & poissonnerie',
    en: 'Meat, poultry & seafood'
  },
  detail: {
    fr: 'Pièces fraîches, volaille, produits de la mer et surgelés.',
    en: 'Fresh cuts, poultry, seafood and frozen selections.'
  },
  image: 'https://images.unsplash.com/photo-1544943910-4c1dc44aab44?auto=format&fit=crop&w=900&q=90'
}, {
  id: 'home-care-and-cleaning',
  name: {
    fr: 'Entretien & nettoyage',
    en: 'Home care & cleaning'
  },
  detail: {
    fr: 'Lessives, nettoyage, papier et soin de la maison.',
    en: 'Detergents, cleaning products, paper goods and daily care.'
  },
  image: 'https://images.unsplash.com/photo-1583947215259-38e31be8751f?auto=format&fit=crop&w=900&q=90'
}, {
  id: 'baby-and-pet-care',
  name: {
    fr: 'Bébé & animaux',
    en: 'Baby & pet care'
  },
  detail: {
    fr: 'Essentiels bébé, alimentation et accessoires animaux.',
    en: 'Baby essentials, pet nutrition and everyday accessories.'
  },
  image: 'https://images.unsplash.com/photo-1589924691995-400dc9ecc119?auto=format&fit=crop&w=900&q=90'
}, {
  id: 'automotive',
  name: {
    fr: 'Automobile',
    en: 'Automotive'
  },
  detail: {
    fr: 'Huiles, pneus, accessoires et entretien véhicule.',
    en: 'Motor oils, tyres, vehicle accessories and maintenance products.'
  },
  image: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&w=900&q=90'
}, {
  id: 'toys-sports-and-travel',
  name: {
    fr: 'Jouets, sport & voyage',
    en: 'Toys, sports & travel'
  },
  detail: {
    fr: 'Jouets, cadeaux, fitness et articles de voyage.',
    en: 'Toys, gifts, fitness essentials and travel goods.'
  },
  image: 'https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?auto=format&fit=crop&w=900&q=90'
}, {
  id: 'business-and-institutional-supply',
  name: {
    fr: 'Approvisionnement professionnel',
    en: 'Business & institutional supply'
  },
  detail: {
    fr: 'Gammes pour restauration, hôtellerie, bureaux et collectivités.',
    en: 'Restaurant, hotel, office and institutional product ranges.'
  },
  image: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=900&q=90'
}];
const drinkFamilies: {
  name: Copy;
  detail: Copy;
  image: string;
}[] = [{
  name: {
    fr: 'Eaux minérales',
    en: 'Mineral water'
  },
  detail: {
    fr: 'Plate · Gazeuse · Grand format',
    en: 'Still · Sparkling · Large format'
  },
  image: 'https://images.unsplash.com/photo-1550505095-81378a674395?auto=format&fit=crop&w=700&q=95'
}, {
  name: {
    fr: 'Boissons sans alcool',
    en: 'Soft drinks'
  },
  detail: {
    fr: 'Cola · Soda · Formats familiaux',
    en: 'Cola · Soda · Family favourites'
  },
  image: 'https://images.unsplash.com/photo-1629203851122-3726ecdf080e?auto=format&fit=crop&w=700&q=95'
}, {
  name: {
    fr: 'Jus & nectars',
    en: 'Juices & nectars'
  },
  detail: {
    fr: 'Jus de fruits · Thé glacé · Smoothies',
    en: 'Fruit juice · Iced tea · Smoothies'
  },
  image: 'https://images.unsplash.com/photo-1621506289937-a8e4df240d0b?auto=format&fit=crop&w=700&q=95'
}, {
  name: {
    fr: 'Café & thé',
    en: 'Coffee & tea'
  },
  detail: {
    fr: 'Café · Thé · Infusions · Cacao',
    en: 'Coffee · Tea · Infusions · Cocoa'
  },
  image: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=700&q=95'
}, {
  name: {
    fr: 'Énergie & sport',
    en: 'Energy & sports'
  },
  detail: {
    fr: 'Énergisants · Boissons isotoniques',
    en: 'Energy drinks · Isotonic refreshments'
  },
  image: 'https://images.unsplash.com/photo-1622543925917-763c34d1a86e?auto=format&fit=crop&w=700&q=95'
}, {
  name: {
    fr: 'Vins & effervescents',
    en: 'Wine & sparkling'
  },
  detail: {
    fr: 'Rouge · Blanc · Rosé · Célébrations',
    en: 'Red · White · Rosé · Celebration'
  },
  image: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?auto=format&fit=crop&w=700&q=95'
}, {
  name: {
    fr: 'Spiritueux & whisky',
    en: 'Spirits & whisky'
  },
  detail: {
    fr: 'Whisky · Gin · Rhum · Bar',
    en: 'Whisky · Gin · Rum · Bar essentials'
  },
  image: 'https://images.unsplash.com/photo-1527281400683-1aae777175f8?auto=format&fit=crop&w=700&q=95'
}, {
  name: {
    fr: 'Formats restauration',
    en: 'Hospitality packs'
  },
  detail: {
    fr: 'Formats choisis pour cafés et événements',
    en: 'Selected formats for cafés and events'
  },
  image: 'https://images.unsplash.com/photo-1513558161293-cdaf765ed2fd?auto=format&fit=crop&w=700&q=95'
}];
export function CategoryPage() {
  const {
    locale
  } = useLocale();
  const text = locale === 'fr' ? {
    eyebrow: 'Tous les rayons',
    title: 'Un hypermarché complet sous un même toit.',
    intro: 'Découvrez l’annuaire complet Société Supermarches Charentais : produits frais, quotidien, équipement, maison, famille, bricolage et approvisionnement professionnel.',
    drinks: 'Boissons',
    drinksTitle: 'De quoi rafraîchir chaque rayon, chaque table et chaque moment.',
    drinksCopy: 'Une offre complète pour les courses du quotidien, l’hôtellerie et les professionnels : eaux fraîches, boissons sans alcool, boissons chaudes, vins et spiritueux.',
    families: 'familles de boissons',
    brands: 'références de marques',
    request: 'Demander des informations',
    imageAlt: 'Rayon de boissons fraîches Centre Leclerc'
  } : {
    eyebrow: 'All departments',
    title: 'A complete hypermarket under one roof.',
    intro: 'Explore the complete Société Supermarches Charentais directory: fresh food, everyday essentials, equipment, home, family, DIY and professional supply.',
    drinks: 'Drinks',
    drinksTitle: 'Refresh every shelf, table and occasion.',
    drinksCopy: 'A complete range for everyday shopping, hospitality and professionals: chilled water, soft drinks, hot drinks, wines and spirits.',
    families: 'drink families',
    brands: 'brand options',
    request: 'Request information',
    imageAlt: 'Chilled drinks department at Centre Leclerc'
  };
  return <SiteLayout data-id="element-78">
      <main className="mx-auto max-w-[1280px] px-5 py-16" data-id="element-79">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#087c83]" data-id="element-80">
          {text.eyebrow}
        </p>
        <h1 className="mt-4 max-w-2xl text-5xl font-extrabold tracking-[-0.05em] text-[#102b43]" data-id="element-81">
          {text.title}
        </h1>
        <p className="mt-5 max-w-2xl leading-7 text-slate-600" data-id="element-82">{text.intro}</p>
        <section id="beverages-wine-and-spirits" className="mt-12 scroll-mt-28 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-id="element-83">
          <div className="grid lg:grid-cols-[0.95fr_1.05fr]" data-id="element-84">
            <div className="bg-[#102b43] p-8 text-white lg:p-11" data-id="element-85">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7ee1df]" data-id="element-86">
                {text.drinks}
              </p>
              <h2 className="mt-5 text-4xl font-extrabold leading-tight tracking-[-0.05em]" data-id="element-87">
                {text.drinksTitle}
              </h2>
              <p className="mt-5 max-w-md leading-7 text-slate-300" data-id="element-88">
                {text.drinksCopy}
              </p>
              <div className="mt-8 grid grid-cols-2 gap-4 border-t border-white/20 pt-6 text-sm" data-id="element-89">
                <div data-id="element-90">
                  <strong className="block text-2xl text-white" data-id="element-91">8</strong>
                  <span className="text-slate-300" data-id="element-92">{text.families}</span>
                </div>
                <div data-id="element-93">
                  <strong className="block text-2xl text-white" data-id="element-94">100+</strong>
                  <span className="text-slate-300" data-id="element-95">{text.brands}</span>
                </div>
              </div>
              <Link to="/contact" className="mt-8 inline-flex rounded-lg bg-[#087c83] px-5 py-3.5 text-xs font-bold uppercase tracking-wider text-white" data-id="element-96">
                {text.request}
              </Link>
            </div>
            <img src="https://images.unsplash.com/photo-1534353473418-4cfa6c56fd38?auto=format&fit=crop&w=1600&q=100" alt={text.imageAlt} className="h-full min-h-[360px] w-full object-cover" data-id="element-97" />
          </div>
          <div className="grid gap-px bg-slate-200 sm:grid-cols-2 lg:grid-cols-4" data-id="element-98">
            {drinkFamilies.map(item => <article key={item.name.fr} className="group bg-white" data-id="element-99">
                <div className="h-44 overflow-hidden bg-[#f7f8fa]" data-id="element-100">
                  <img src={item.image} alt={item.name[locale]} loading="lazy" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" data-id="element-101" />
                </div>
                <div className="p-5" data-id="element-102">
                  <h3 className="font-extrabold text-[#102b43]" data-id="element-103">
                    {item.name[locale]}
                  </h3>
                  <p className="mt-1 text-sm leading-5 text-slate-500" data-id="element-104">
                    {item.detail[locale]}
                  </p>
                  <Link to="/contact" className="mt-4 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#087c83]" data-id="element-105">
                    {text.request} <ArrowRightIcon size={13} data-id="element-106" />
                  </Link>
                </div>
              </article>)}
          </div>
        </section>
        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-107">
          {departments.map(item => <article id={item.id} key={item.id} className="scroll-mt-28 overflow-hidden rounded-xl bg-white shadow-sm" data-id="element-108">
              <img src={item.image} alt={item.name[locale]} loading="lazy" className="h-48 w-full object-cover" data-id="element-109" />
              <div className="p-5" data-id="element-110">
                <h2 className="font-extrabold text-[#102b43]" data-id="element-111">
                  {item.name[locale]}
                </h2>
                <p className="mt-2 text-sm leading-5 text-slate-500" data-id="element-112">
                  {item.detail[locale]}
                </p>
                <Link to="/contact" className="mt-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#087c83]" data-id="element-113">
                  {text.request} <ArrowRightIcon size={14} data-id="element-114" />
                </Link>
              </div>
            </article>)}
        </div>
      </main>
    </SiteLayout>;
}